import initSqlJs, { type SqlJsStatic } from 'sql.js';
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import { readDatabase, writeDatabase } from './indexedDb';
import type { SemanticDimension, SemanticPrompt } from './contract';
import { DEFAULT_SUBMISSION_PIPELINE, processingCharacterLimits, submissionPipeline, type SystemPipeline, type SystemPipelineDefinition } from './pipeline';

const FILE = 'graphite-semantic-config-v1.sqlite';
const RETIRED_DIMENSION_KEYS = ['desire', 'goal', 'duty', 'time_suitability', 'people', 'projects', 'roles', 'places', 'resources', 'topics'];
let sqlitePromise: Promise<SqlJsStatic> | null = null;

function withoutLegacyBlocks(value: unknown): SystemPipelineDefinition | null {
  if (!value || typeof value !== 'object') return null;
  const definition = value as Record<string, unknown>;
  if (!Array.isArray(definition.stages) || !definition.parameters || typeof definition.parameters !== 'object') return null;
  const legacyExecutionOrder = Number(definition.version || 1) < 5;
  const canonicalStages = new Map(DEFAULT_SUBMISSION_PIPELINE.stages.map((stage) => [stage.id, stage]));
  return {
    version: 5,
    runtime: 'submission-enrichment',
    parameters: { ...DEFAULT_SUBMISSION_PIPELINE.parameters, ...definition.parameters } as SystemPipelineDefinition['parameters'],
    stages: definition.stages.flatMap((value) => {
      if (!value || typeof value !== 'object') return [];
      const stage = value as Record<string, unknown>;
      return [{
        id: String(stage.id), title: String(stage.title), description: String(stage.description || ''),
        mode: legacyExecutionOrder && canonicalStages.has(String(stage.id)) ? canonicalStages.get(String(stage.id))!.mode : stage.mode === 'parallel' || stage.mode === 'conditional' ? stage.mode : 'sequential',
        enabled: stage.enabled !== false,
        dependsOn: legacyExecutionOrder && canonicalStages.has(String(stage.id)) ? canonicalStages.get(String(stage.id))!.dependsOn : Array.isArray(stage.dependsOn) ? stage.dependsOn.map(String) : [],
      }];
    }),
  };
}

export async function ensureSemanticDatabase() {
  sqlitePromise ||= initSqlJs({ locateFile: () => wasmUrl });
  const SQL = await sqlitePromise;
  const stored = await readDatabase(FILE);
  const db = stored ? new SQL.Database(stored) : new SQL.Database();
  db.run(`
    CREATE TABLE IF NOT EXISTS dimensions (
      id INTEGER PRIMARY KEY AUTOINCREMENT, key TEXT NOT NULL UNIQUE,
      name TEXT NOT NULL, prompt TEXT NOT NULL DEFAULT '', enabled INTEGER NOT NULL DEFAULT 1
    );
    CREATE TABLE IF NOT EXISTS prompts (
      id INTEGER PRIMARY KEY AUTOINCREMENT, key TEXT NOT NULL UNIQUE,
      purpose TEXT NOT NULL, template TEXT NOT NULL, model_hint TEXT
    );
    CREATE TABLE IF NOT EXISTS system_pipelines (
      id INTEGER PRIMARY KEY, key TEXT NOT NULL UNIQUE, name TEXT NOT NULL,
      description TEXT NOT NULL DEFAULT '', trigger_key TEXT NOT NULL,
      enabled INTEGER NOT NULL DEFAULT 1, revision INTEGER NOT NULL DEFAULT 1,
      definition TEXT NOT NULL
    );
  `);
  const dimensionColumns = new Set((db.exec('PRAGMA table_info(dimensions)')[0]?.values || []).map((row) => String(row[1])));
  if (!dimensionColumns.has('prompt')) {
    if (dimensionColumns.has('description')) db.run('ALTER TABLE dimensions RENAME COLUMN description TO prompt');
    else db.run("ALTER TABLE dimensions ADD COLUMN prompt TEXT NOT NULL DEFAULT ''");
  }
  const retired = RETIRED_DIMENSION_KEYS.map(() => '?').join(',');
  db.run(`DELETE FROM dimensions WHERE key IN (${retired})`, RETIRED_DIMENSION_KEYS);
  db.run("DELETE FROM prompts WHERE key='select.alias.v1'");
  db.run(
    'INSERT OR IGNORE INTO system_pipelines(id,key,name,description,trigger_key,enabled,revision,definition) VALUES (1,?,?,?,?,1,1,?)',
    ['submission.enrichment.v1', 'Обработка импульса', 'Встроенная offline-схема обработки Graphite.', 'capture.submit', JSON.stringify(DEFAULT_SUBMISSION_PIPELINE)],
  );
  const storedPipelines = db.exec('SELECT id,definition FROM system_pipelines')[0];
  for (const [id, raw] of storedPipelines?.values || []) {
    try {
      const definition = withoutLegacyBlocks(JSON.parse(String(raw)));
      if (definition) db.run('UPDATE system_pipelines SET definition=? WHERE id=?', [JSON.stringify(definition), Number(id)]);
    } catch { /* A later server sync can replace an unreadable legacy row. */ }
  }
  await writeDatabase(FILE, db.export());
  db.close();
}

async function openSemanticDatabase() {
  sqlitePromise ||= initSqlJs({ locateFile: () => wasmUrl });
  const SQL = await sqlitePromise;
  const stored = await readDatabase(FILE);
  return stored ? new SQL.Database(stored) : new SQL.Database();
}

export async function semanticConfig(): Promise<{ dimensions: SemanticDimension[]; prompts: SemanticPrompt[]; pipelines: SystemPipeline[] }> {
  await ensureSemanticDatabase();
  const db = await openSemanticDatabase();
  const rows = (sql: string) => {
    const result = db.exec(sql)[0];
    return result ? result.values.map((values) => Object.fromEntries(result.columns.map((column, index) => [column, values[index]]))) : [];
  };
  const dimensions = rows('SELECT * FROM dimensions WHERE enabled=1 ORDER BY id').map((row) => ({
    id: Number(row.id), key: String(row.key), name: String(row.name), prompt: String(row.prompt || ''), enabled: Boolean(row.enabled),
  }));
  const prompts = rows('SELECT * FROM prompts ORDER BY id').map((row) => ({
    id: Number(row.id), key: String(row.key), purpose: String(row.purpose), template: String(row.template), modelHint: row.model_hint ? String(row.model_hint) : null,
  }));
  const pipelines = rows('SELECT * FROM system_pipelines ORDER BY id').flatMap((row) => {
    try {
      return [{
        id: Number(row.id), key: String(row.key), name: String(row.name), description: String(row.description || ''),
        triggerKey: String(row.trigger_key), enabled: Boolean(row.enabled), revision: Number(row.revision || 1),
        definition: JSON.parse(String(row.definition)) as SystemPipelineDefinition,
      }];
    } catch { return []; }
  });
  db.close();
  return { dimensions, prompts, pipelines };
}

export async function configuredDocumentNoteLimit() {
  const config = await semanticConfig();
  return processingCharacterLimits(submissionPipeline(config.pipelines)).noteChars;
}

export async function syncSemanticConfig(endpoint: string, token: string): Promise<void> {
  await ensureSemanticDatabase();
  const base = endpoint.replace(/\/$/, '');
  const headers = { Authorization: `Bearer ${token}` };
  const [dimensionsResponse, promptsResponse, pipelinesResponse] = await Promise.all([
    fetch(`${base}/v1/dimensions`, { headers }), fetch(`${base}/v1/prompts`, { headers }),
    fetch(`${base}/v1/system-pipelines`, { headers }),
  ]);
  if (!dimensionsResponse.ok || !promptsResponse.ok || !pipelinesResponse.ok) throw new Error('Справочники недоступны');
  const dimensionsBody = await dimensionsResponse.json() as { items: Array<Record<string, unknown>> };
  const promptsBody = await promptsResponse.json() as { items: Array<Record<string, unknown>> };
  const pipelinesBody = await pipelinesResponse.json() as { items: Array<Record<string, unknown>> };
  const db = await openSemanticDatabase();
  db.run('BEGIN');
  try {
    db.run('DELETE FROM dimensions');
    db.run('DELETE FROM prompts');
    db.run('DELETE FROM system_pipelines');
    for (const row of dimensionsBody.items) db.run(
      'INSERT INTO dimensions(id,key,name,prompt,enabled) VALUES (?,?,?,?,?)',
      [Number(row.id), String(row.key), String(row.name), String(row.prompt || row.description || ''), row.enabled ? 1 : 0],
    );
    for (const row of promptsBody.items) db.run(
      'INSERT INTO prompts(id,key,purpose,template,model_hint) VALUES (?,?,?,?,?)',
      [Number(row.id), String(row.key), String(row.purpose), String(row.template), row.model_hint ? String(row.model_hint) : null],
    );
    for (const row of pipelinesBody.items) db.run(
      'INSERT INTO system_pipelines(id,key,name,description,trigger_key,enabled,revision,definition) VALUES (?,?,?,?,?,?,?,?)',
      [Number(row.id), String(row.key), String(row.name), String(row.description || ''), String(row.trigger_key), row.enabled ? 1 : 0, Number(row.revision || 1), JSON.stringify(withoutLegacyBlocks(row.definition) || DEFAULT_SUBMISSION_PIPELINE)],
    );
    db.run('COMMIT');
    await writeDatabase(FILE, db.export());
  } catch (error) {
    db.run('ROLLBACK');
    throw error;
  } finally {
    db.close();
  }
}
